import { FC, useCallback, useEffect, useState } from 'react';

import { MultiSelectDropdown } from '@components/index';
import { useLazyGetAgentsQuery } from '@features/lead-management/queries/LeadManagementQuery';
import useNotify from '@hooks/notify';
import { DropdownOption } from 'types/dropdown';
import { Agent } from 'types/lead-management';

import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME,
  UNASSIGNED_ASSIGNEE
} from '../constants/common';

interface AgentSelectorProps {
  setSelectedAgents: (
    selectedItems: DropdownOption[],
    options: DropdownOption[]
  ) => void;
  selectedAgents: DropdownOption[];
  organizationId: string;
  showUnassignedAgent?: boolean;
}

const AgentSelector: FC<AgentSelectorProps> = ({
  selectedAgents,
  setSelectedAgents,
  organizationId,
  showUnassignedAgent = false
}) => {
  const { createErrorAlert } = useNotify();
  const [agents, setAgents] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [getAgents] = useLazyGetAgentsQuery();

  const unAssignedAgent = {
    id: UNASSIGNED_ASSIGNEE.id,
    value: UNASSIGNED_ASSIGNEE.id,
    label: UNASSIGNED_ASSIGNEE.name
  };

  const fetchAgents = useCallback(
    async () => {
      try {
        setIsLoading(true);
        await getAgents({
          organizationId
        })
          .unwrap()
          .then((res) => {
            if (res?.sales_agents) {
              const mappedAgentResponse = res.sales_agents.map(
                (agent: Agent) => ({
                  id: agent.user_id,
                  value: agent.user_id,
                  label: agent.name
                })
              );
              const mappedAgentsList = showUnassignedAgent
                ? [unAssignedAgent, ...mappedAgentResponse]
                : mappedAgentResponse;
              setAgents(mappedAgentsList);
              setSelectedAgents(mappedAgentsList, mappedAgentsList);
            }
          });
      } catch (error) {
        createErrorAlert('Error fetching agents');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAgents, organizationId]
  );

  const handleSelectOption = (selectedItems: DropdownOption[]) => {
    if (selectedItems.length === 0) {
      if (showUnassignedAgent) {
        setSelectedAgents([unAssignedAgent], agents);
      } else {
        createErrorAlert('Please select at least one agent');
      }
    } else {
      setSelectedAgents(selectedItems, agents);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return (
    <>
      <label className="mb-2 block text-sm font-semibold text-darkText">
        Assigned Agent/Staff
      </label>
      <MultiSelectDropdown
        label="Select Agent"
        searchBarPlaceholder="Search by Agent name"
        options={agents}
        onChange={(elements) => handleSelectOption(elements)}
        selectedOptions={selectedAgents}
        className="min-w-232"
        chipToolTipId="agent-tooltip"
        isLoading={isLoading}
        dropDownHeaderStyle={`${INPUT_BASE_CLASSNAME} ${INPUT_FOCUS_CLASSNAME}`}
        chipClassName="!bg-grey10"
        plusNChipBgColor="bg-grey10"
      />
    </>
  );
};
export default AgentSelector;
